/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */

import { Theme } from "@mui/material/styles";
import { makeStyles, styled } from "@mui/styles";
import { Drawer, createTheme } from "@mui/material";
import { Tokens } from "@jda/lui-common-component-library-mui5";

const drawerWidth = 250;
const theme = createTheme();

interface StyledDrawerProps {
  open: boolean;
}

const StyledDrawer = styled(Drawer)<StyledDrawerProps>(({ open }) => ({
  width: open ? drawerWidth : theme.spacing(5),
  height: theme.spacing(100),
  whiteSpace: "nowrap",
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  "& .MuiPaper-root": {
    width: drawerWidth,
    backgroundColor: "#060E2A",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  "& li.MuiListItem-root": {
    padding: 0,
    "&  > .MuiListItemButton-root": {
      paddingTop: Tokens.spacing["$sp-3"],
      paddingBottom: Tokens.spacing["$sp-3"],
      ".MuiListItemIcon-root:nth-of-type(2) > .MuiSvgIcon-root": {
        fontSize: Tokens.spacing["$sp-4"],
      },
      ".MuiListItemIcon-root .MuiSvgIcon-root": {
        fontSize: Tokens.spacing["$sp-8"],
      },
    },
  },
  "& .MuiCollapse-wrapper li.MuiListItem-root > .MuiListItemButton-root": {
    paddingTop: Tokens.spacing["$sp-2"],
    paddingBottom: Tokens.spacing["$sp-2"],
    "& :hover": {
      backgroundColor: "transparent",
    },
  },
  ...(!open && {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(5),
    },
    "& .MuiPaper-root": {
      width: theme.spacing(5),
      overflowX: "hidden",
      backgroundColor: "#060E2A",
      textAlign: "center",
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      [theme.breakpoints.up("sm")]: {
        // width: theme.spacing(5),
      },
    },
  }),
}));

const useStyles = makeStyles((theme: Theme) => ({
  drawerWrapper: {
    maxWidth: 500,
    overflow: "hidden",
    position: "absolute",
  },
  listItemText: {
    color: 'white',
  },
  
  byLogo: {
    marginBottom: theme.spacing(1),
  },
  listItemButton: {
    marginTop: theme.spacing(1),
    color: theme?.palette?.mode === "dark" ? theme.palette.text.primary : theme.palette.background.default,
    borderRadius: theme.spacing(0.75),
    width:"100%",
    justifyContent: "flex-start",
    '& svg': {
      color: 'white',
    },
    "&:hover": {
      backgroundColor:"#5b84ff1f !important"
    },
    "&.selectedListItem": {
      backgroundColor: "#4473ff62",
    },
  },
  listItemIcon: {
    color: 'white',
  },
  iconWithText: {
    fontFamily: "Calibre, Roboto, sans-serif",
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(1.5)
  },
  leftArrowIcon: {
    color: theme?.palette?.mode === "dark" ? theme.palette.text.primary : theme.palette.background.default,
  },
  drawerDivider: {
    marginTop: theme.spacing(0.25),
    borderColor: theme.palette.divider,
  },
  rightArrowIcon: {
    right: theme.spacing(3),
    transform: "rotate(180deg)",
    color: theme?.palette?.mode === "dark" ? theme.palette.text.primary : theme.palette.background.default,
  },
  listItemButtonBottom: {
    position: "absolute",
    bottom: 0,
    width: "100%",
    display: "flex",
    alignItems: "center",
    color: theme?.palette?.mode === "dark" ? theme.palette.text.primary : theme.palette.background.default,
  },
  listItemIconButtonBottom: {
    right: theme.spacing(1.5),
    color: theme?.palette?.mode === "dark" ? theme.palette.text.primary : theme.palette.background.default,
    "& :hover": {
      backgroundColor:"#5b84ff1f !important"
    },
  },
  title: {
    padding: theme.spacing(2),
    position: "fixed"
  }
}));

export { StyledDrawer, useStyles };
